/**
 * Column selectors need a lot of Contentstack settings lined up correctly
 * before they can render anything sensible, and none of it is obvious from the
 * entry. This resolves the settings both selectors require and reports the
 * first thing that's missing.
 *
 * @returns `{ ok: true, data }` with everything needed to render, or
 *   `{ ok: false, error }` describing what was wrong.
 */
export function resolveColumnOption(settings, productData) {
  const optionName = settings?.option;
  if (!optionName || !productData?.shopifyProduct) {
    return { ok: false, error: 'Missing key variant or product data' };
  }

  const option = productData.shopifyProduct?.options?.find(
    (o) => o?.name === optionName
  );
  if (!option || !option.name || !Array.isArray(option.values)) {
    return { ok: false, error: `Option "${optionName}" not found or invalid` };
  }

  const columnSettings = settings?.modular_blocks?.[0]?.column;
  const baseOptionValue = columnSettings?.base_option_value;
  if (!baseOptionValue || !option.values.includes(baseOptionValue)) {
    return {
      ok: false,
      error: 'A base option value must be specified and valid'
    };
  }

  return {
    ok: true,
    data: { optionName, option, columnSettings, baseOptionValue }
  };
}

/**
 * The value currently selected for `option`, falling back to the option's first
 * value when the variant doesn't carry it.
 *
 * `resolveColumnOption` guarantees `option.values` contains `base_option_value`,
 * so the fallback always resolves to something real.
 */
export function selectedOptionValue(selectedVariant, option) {
  return (
    selectedVariant?.selectedOptions?.find((o) => o?.name === option.name)
      ?.value ?? option.values[0]
  );
}
